import React, { useState } from 'react';
import {
  Box, Container, Grid, Paper, Tab, Tabs, Typography, Table, TableHead,
  TableBody, TableRow, TableCell, Chip, Alert, Skeleton,
} from '@mui/material';
import { CameraAlt, Instagram, Facebook, TrendingUp } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useUrlIntState } from '../hooks/useUrlState';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from 'recharts';
import { metaAPI } from '../services/api';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const MetaDashboard: React.FC = () => {
  const [active, setActive] = useUrlIntState('meta_tab', 0);

  const { data: igOverview } = useQuery({ queryKey: ['meta-ig-overview'], queryFn: metaAPI.getInstagramOverview, staleTime: 60000 });
  const { data: igHistory } = useQuery({ queryKey: ['meta-ig-history'], queryFn: () => metaAPI.getInstagramHistory(90), staleTime: 60000 });
  const { data: igMedia } = useQuery({ queryKey: ['meta-ig-media'], queryFn: () => metaAPI.getInstagramMedia(50), staleTime: 60000 });
  const { data: fbOverview } = useQuery({ queryKey: ['meta-fb-overview'], queryFn: metaAPI.getFacebookOverview, staleTime: 60000 });
  const { data: fbHistory } = useQuery({ queryKey: ['meta-fb-history'], queryFn: () => metaAPI.getFacebookHistory(90), staleTime: 60000 });
  const { data: fbPosts } = useQuery({ queryKey: ['meta-fb-posts'], queryFn: () => metaAPI.getFacebookPosts(50), staleTime: 60000 });
  const { data: igDemographics } = useQuery({ queryKey: ['meta-ig-demographics'], queryFn: metaAPI.getInstagramDemographics, staleTime: 60000 });
  const { data: fbDemographics } = useQuery({ queryKey: ['meta-fb-demographics'], queryFn: metaAPI.getFacebookDemographics, staleTime: 60000 });

  const notConfigured = igOverview && !igOverview.configured && fbOverview && !fbOverview.configured;

  if (notConfigured) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box mb={4}>
          <SectionTitle sub="&gt; Instagram + Facebook analytics, audience growth_">
            META :: SOCIAL CONSOLE
          </SectionTitle>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Configure META_ACCESS_TOKEN to start syncing Instagram and Facebook data.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <SectionTitle sub="&gt; Instagram + Facebook analytics, audience growth_">
          META :: SOCIAL CONSOLE
        </SectionTitle>
      </Box>

      {/* KPI row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'IG FOLLOWERS', value: fmt(igOverview?.followers_count) },
          { label: 'IG POSTS', value: fmt(igOverview?.media_count) },
          { label: 'FB FANS', value: fmt(fbOverview?.fan_count) },
          { label: 'FB FOLLOWERS', value: fmt(fbOverview?.followers_count) },
        ].map((kpi) => (
          <Grid size={{ xs: 6, md: 3 }} key={kpi.label}>
            <Panel label={kpi.label}>
              <Typography variant="h4" fontWeight={700}>{kpi.value}</Typography>
            </Panel>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={active} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<Instagram />} label="Instagram" />
          <Tab icon={<Facebook />} label="Facebook" />
        </Tabs>
      </Paper>

      {/* Instagram tab */}
      <TabPanel value={active} index={0}>
        <Panel label="INSTAGRAM FOLLOWER HISTORY">
          {igHistory?.available ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={igHistory.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Area type="monotone" dataKey="followers_count" stroke="#00ff41" fill="#00ff41" fillOpacity={0.2} name="Followers" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Skeleton variant="rectangular" height={300} />}
        </Panel>

        <Panel label="RECENT MEDIA" sx={{ mt: 3 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CAPTION</TableCell>
                  <TableCell>TYPE</TableCell>
                  <TableCell>POSTED</TableCell>
                  <TableCell align="right">LIKES</TableCell>
                  <TableCell align="right">COMMENTS</TableCell>
                  <TableCell align="right">VIEWS</TableCell>
                  <TableCell align="right">REACH</TableCell>
                  <TableCell align="right">SAVES</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(igMedia?.media || []).map((m: any) => (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 300 }}>
                        {m.caption?.substring(0, 80) || 'No caption'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={m.media_type || '—'} size="small" />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.timestamp ? new Date(m.timestamp).toLocaleDateString() : '—'}</TableCell>
                    <TableCell align="right">{fmt(m.like_count)}</TableCell>
                    <TableCell align="right">{fmt(m.comments_count)}</TableCell>
                    <TableCell align="right">{fmt(m.views)}</TableCell>
                    <TableCell align="right">{fmt(m.reach)}</TableCell>
                    <TableCell align="right">{fmt(m.saved)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>

        {igDemographics?.available && (
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {igDemographics.demographics.country && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Panel label="IG AUDIENCE BY COUNTRY">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={igDemographics.demographics.country.slice(0, 8)}
                        dataKey="value"
                        nameKey="dimension"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(e: any) => e.dimension}
                      >
                        {igDemographics.demographics.country.slice(0, 8).map((_: any, i: number) => (
                          <Cell key={i} fill={['#00ff41','#00ffff','#ff00ff','#ffff00','#ff6600','#6666ff','#ff3366','#33cc99'][i % 8]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Panel>
              </Grid>
            )}
            {igDemographics.demographics.gender_age && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Panel label="IG AUDIENCE BY GENDER / AGE">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={igDemographics.demographics.gender_age.slice(0, 12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dimension" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#00ff41" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              </Grid>
            )}
          </Grid>
        )}
      </TabPanel>

      {/* Facebook tab */}
      <TabPanel value={active} index={1}>
        <Panel label="FACEBOOK PAGE HISTORY">
          {fbHistory?.available ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={fbHistory.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Area type="monotone" dataKey="fan_count" stroke="#00ff41" fill="#00ff41" fillOpacity={0.2} name="Fans" />
                <Area type="monotone" dataKey="followers_count" stroke="#00ffff" fill="#00ffff" fillOpacity={0.1} name="Followers" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Skeleton variant="rectangular" height={300} />}
        </Panel>

        <Panel label="RECENT POSTS" sx={{ mt: 3 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>MESSAGE</TableCell>
                  <TableCell>TYPE</TableCell>
                  <TableCell>POSTED</TableCell>
                  <TableCell align="right">LIKES</TableCell>
                  <TableCell align="right">COMMENTS</TableCell>
                  <TableCell align="right">SHARES</TableCell>
                  <TableCell align="right">CLICKS</TableCell>
                  <TableCell align="right">REACH</TableCell>
                  <TableCell align="right">ENGAGEMENTS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(fbPosts?.posts || []).map((p: any) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 300 }}>
                        {p.message?.substring(0, 80) || 'No message'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={p.post_type || '—'} size="small" />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.created_time ? new Date(p.created_time).toLocaleDateString() : '—'}</TableCell>
                    <TableCell align="right">{fmt(p.likes)}</TableCell>
                    <TableCell align="right">{fmt(p.comments)}</TableCell>
                    <TableCell align="right">{fmt(p.shares)}</TableCell>
                    <TableCell align="right">{fmt(p.clicks)}</TableCell>
                    <TableCell align="right">{fmt(p.reach)}</TableCell>
                    <TableCell align="right">{fmt(p.engagements)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>

        {fbDemographics?.available && (
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {fbDemographics.demographics.country && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Panel label="FB FANS BY COUNTRY">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={fbDemographics.demographics.country.slice(0, 8)}
                        dataKey="value"
                        nameKey="dimension"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(e: any) => e.dimension}
                      >
                        {fbDemographics.demographics.country.slice(0, 8).map((_: any, i: number) => (
                          <Cell key={i} fill={['#00ff41','#00ffff','#ff00ff','#ffff00','#ff6600','#6666ff','#ff3366','#33cc99'][i % 8]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Panel>
              </Grid>
            )}
            {fbDemographics.demographics.gender_age && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Panel label="FB FANS BY GENDER / AGE">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={fbDemographics.demographics.gender_age.slice(0, 12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dimension" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#00ffff" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              </Grid>
            )}
          </Grid>
        )}
      </TabPanel>
    </Container>
  );
};

export default MetaDashboard;
